import type { CSSProperties, FC, ReactNode } from 'react'
import type { Capability } from '@/constants/platform'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { FigLabel } from '@/components/generals/fig-label'
import { PointCloud } from '@/components/generals/point-cloud'
import { Reveal } from '@/components/generals/reveal'

interface CapabilitySectionProps {
    capability: Capability
    fig: string
}

const delay = (value: number): CSSProperties => ({ '--delay': `${value}ms` } as CSSProperties)

/**
 * One full-height platform sheet: figure label, headline, two paragraphs and five spec rows on the
 * left; a slowly orbiting point cloud on the right that speeds up under the pointer and can be dragged.
 */
export const CapabilitySection: FC<CapabilitySectionProps> = ({ capability, fig }): ReactNode => {
    const [view, setView] = useState({ yaw: 36, pitch: 30 })
    const last = capability.headline.length - 1
    const titleId = `${capability.id}-title`

    return (
        <section id={capability.id} aria-labelledby={titleId} className='relative scroll-mt-16'>
            <Reveal className='relative desk:flex desk:min-h-svh desk:items-stretch'>
                <span className='draw-x absolute inset-x-0 top-0 h-px bg-hairline' aria-hidden='true' />

                <div className='grid w-full desk:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]'>
                    <div className='flex flex-col justify-center px-gutter py-16 desk:py-24 desk:pr-16'>
                        <FigLabel fig={fig} title={capability.name} detail={capability.figure} note={`Step ${capability.number} of 04 / ${capability.caption}`} />

                        <h2 id={titleId} className='display-lg mt-10'>
                            {capability.headline.map((line, index) => (
                                <span key={line} className='reveal-line'>
                                    <span className={cn('reveal-inner', index === last ? 'text-dim' : 'text-ink')} style={delay(100 + index * 90)}>
                                        {line}
                                    </span>
                                </span>
                            ))}
                        </h2>

                        <div className='reveal-fade mt-8 grid max-w-[640px] gap-5 text-[15px] leading-[1.6] text-ink/85 sm:grid-cols-2 sm:gap-8' style={delay(320)}>
                            <p>
                                {capability.body}
                            </p>

                            <p>
                                {capability.detail}
                            </p>
                        </div>

                        <dl className='reveal-fade mt-10 max-w-[640px] border-t border-hairline' style={delay(420)}>
                            {capability.specs.map(spec => (
                                <div key={spec.label} className='flex items-baseline justify-between gap-6 border-b border-hairline py-3'>
                                    <dt className='hud shrink-0 text-mute'>
                                        {spec.label}
                                    </dt>

                                    <dd className='hud text-right text-ink'>
                                        {spec.value}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </div>

                    <div className='relative border-t border-hairline desk:border-l desk:border-t-0'>
                        <PointCloud
                            shape={capability.shape}
                            label={capability.previewLabel}
                            budget='preview'
                            rotate='always'
                            rotateSpeed={4}
                            hoverBoost={4.5}
                            interactive
                            effects
                            frame
                            grid
                            onFrame={info => {
                                const yaw = Math.round(info.yaw) % 360
                                const pitch = Math.round(info.pitch)

                                setView(current => (current.yaw === yaw && current.pitch === pitch ? current : { yaw, pitch }))
                            }}
                            className='aspect-[4/3] w-full desk:absolute desk:inset-0 desk:aspect-auto'
                        >
                            <div className='pointer-events-none absolute inset-0 z-[2]' aria-hidden='true'>
                                <p className='hud absolute left-5 top-5 text-ink'>
                                    Fig. {fig}.1
                                </p>

                                <p className='hud absolute right-gutter top-5 text-right text-mute'>
                                    Yaw {String(view.yaw).padStart(3, '0')} / Pitch {String(view.pitch).padStart(2, '0')}
                                </p>

                                <p className='hud absolute bottom-5 left-5 text-mute'>
                                    {capability.caption}
                                </p>

                                <p className='hud absolute bottom-5 right-gutter hidden text-mute sm:block'>
                                    Drag to orbit
                                </p>
                            </div>
                        </PointCloud>
                    </div>
                </div>
            </Reveal>
        </section>
    )
}
